import clsx from "clsx";
import { isEmpty, snakeCase } from "lodash";
import { MouseEvent } from "react";

import {
  Card,
  KeyValueList,
  RenderWhen,
  ThreatStatusLabel,
  Time
} from "../../../../../../components";
import { StaticAnalysis as StaticAnalysisType } from "../../../../@types";
import { CardSubtitle } from "../../components";

import { getClassificationByValue } from "../../../../../../lib/enums/ThreatStatusEnum";
import { SECTIONS } from "../../../../consts";
import {
  useGetSampleAnalysisStatus,
  useLocalSampleSummaryParams
} from "../../../../hooks";
import styles from "../analysis-card.module.scss";

type Props = {
  decisive?: boolean;
  data?: StaticAnalysisType;
  onClick: (sections: string) => void;
};

const StaticAnalysis = ({ data, decisive, onClick }: Props) => {
  const { hash } = useLocalSampleSummaryParams();
  const { data: { tc = undefined } = {} } = useGetSampleAnalysisStatus(hash);

  const classificationValue = getClassificationByValue(
    data?.classification
  )?.toLowerCase();

  const handleOnClick = (event: MouseEvent) => {
    event.stopPropagation();
    onClick(SECTIONS.TI_CORE.CLASSIFICATION);
  };

  return (
    <Card
      className={clsx(
        decisive && styles[`decisive_reason_${classificationValue}`]
      )}
      icon="static-analysis"
      title="Static Analysis"
      subtitle={
        <CardSubtitle
          impactsFinalClassification
          hasData={!isEmpty(data)}
          additionalInfo={tc?.additional_information}
        >
          <div className="flex align-center flex-gap-1">
            <ThreatStatusLabel
              className={styles.analysis_card_subtitle_classification}
              threatStatus={data?.classification}
              showOrb={false}
              showColor
            />
            <RenderWhen condition={Boolean(decisive)}>
              <span
                className={clsx(
                  styles.decisive_reason_label,
                  styles[classificationValue]
                )}
              >
                (Decisive)
              </span>
            </RenderWhen>
          </div>
        </CardSubtitle>
      }
      data-testid="report_summary_static_analysis"
    >
      <KeyValueList
        className={styles.analysis_card_content_wrapper}
        onClick={handleOnClick}
        data-testid="report_summary_static_analysis_list"
      >
        <KeyValueList.Item
          className={styles.list_item_static_analysis}
          keyClassName={clsx("font-medium", "white-space-nowrap")}
          label="Analysis Time: "
          value={
            <Time className="overflow-ellipsis" time={data?.analysis_time} />
          }
          data-testid="report_summary_static_analysis_analysis_time"
        />
        <>
          {data?.scan_results?.map(result => (
            <KeyValueList.Item
              key={result.name}
              className={styles.list_item_static_analysis}
              keyClassName={clsx("font-normal", "width-60-pc")}
              label={result.name}
              value={
                <ThreatStatusLabel
                  className={clsx(
                    styles.list_item_value,
                    "width-40-pc",
                    styles.threat_status
                  )}
                  showOrb
                  threatStatus={result.classification}
                />
              }
              data-testid={`report_summary_static_analysis_${snakeCase(
                result.name
              )}`}
            />
          ))}
        </>
      </KeyValueList>
    </Card>
  );
};

export default StaticAnalysis;
